
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux';
import './AuditDashboard.css'
import OrgImg from "../../../assets/audit_assets/Audit_dashboard/OrgIcon.png"
import VendorIcon from "../../../assets/audit_assets/Audit_dashboard/VendorIcon.png"
import BranchesIcon from "../../../assets/audit_assets/Audit_dashboard/BranchesIcon.png"
import TotalAuditIcon from "../../../assets/audit_assets/Audit_dashboard/TotalAuditIcon.png"
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { getClientWiseAudit } from "../../../services/audit_redux/audit_actions/Audit.action";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const STATUS_LABEL = {
  Audit: "Created",
  "Vendor-In-Progress": "Vendor working",
  "Audit-Pending": "Submitted",
  "Auditor-In-progress": "Under review",
  Completed: "Completed",
  "Validity-expired": "Validity expired",
};

function getSessionUser() {
  try {
    return JSON.parse(sessionStorage.getItem("user") || "{}") || {};
  } catch {
    return {};
  }
}

function getAssignedClientUnits() {
  try {
    const mapped = JSON.parse(getSessionUser().client_and_unit || "[]");
    return Array.isArray(mapped) ? mapped : [];
  } catch {
    return [];
  }
}

function getLockedClientId() {
  const stored = sessionStorage.getItem("selectedClient");
  if (stored && stored !== "null") return stored;
  return getAssignedClientUnits()[0]?.client_id || "";
}

function getAssignedUnits(clientId) {
  const entry = getAssignedClientUnits().find(
    (item) => String(item.client_id) === String(clientId)
  );
  return (entry?.unit_id || []).map(String);
}

function toYearMonth(label) {
  const raw = String(label || "").trim();
  const iso = raw.match(/^(\d{4})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}`;
  const named = raw.match(/^([A-Za-z]{3})-(\d{4})$/);
  if (!named) return "";
  const index = MONTHS.findIndex((month) => month.toLowerCase() === named[1].toLowerCase());
  if (index < 0) return "";
  return `${named[2]}-${String(index + 1).padStart(2, "0")}`;
}

function monthLabel(yearMonth) {
  const match = String(yearMonth || "").match(/^(\d{4})-(\d{2})$/);
  if (!match) return yearMonth || "";
  return `${MONTHS[Number(match[2]) - 1]}-${match[1]}`;
}

function statusBucket(status) {
  if (status === "Completed") return "completed";
  if (status === "Audit-Pending" || status === "Validity-expired") return "pending";
  return "inProgress";
}

function formatWhen(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { day: "", time: "" };
  return {
    day: date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
    time: date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
  };
}

function percent(part, total) {
  if (!total) return 0;
  return Math.round((part / total) * 100);
}

function ClientAuditDashboard() {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [vendorId, setVendorId] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");

  useEffect(() => {
    const clientId = getLockedClientId();
    if (!clientId) {
      setRows([]);
      return;
    }
    let active = true;
    (async () => {
      const response = await dispatch(getClientWiseAudit(clientId));
      if (!active) return;
      if (!response?.success) {
        setRows([]);
        return;
      }
      const assigned = getAssignedUnits(clientId);
      const list = Array.isArray(response.data?.data) ? response.data.data : [];
      setRows(list.filter((item) => !assigned.length || assigned.includes(String(item.unit_id))));
    })();
    return () => {
      active = false;
    };
  }, [dispatch]);

  const branchCount = useMemo(() => {
    const assigned = getAssignedUnits(getLockedClientId());
    if (assigned.length) return assigned.length;
    return new Set(rows.map((row) => String(row.unit_id || "")).filter(Boolean)).size;
  }, [rows]);

  const vendorOptions = useMemo(() => {
    const seen = new Map();
    rows.forEach((row) => {
      const id = String(row.vendor_id || row.vendor_name || "");
      if (!id || seen.has(id)) return;
      seen.set(id, row.vendor_name || id);
    });
    return Array.from(seen, ([id, label]) => ({ id, label }));
  }, [rows]);

  const chartRows = vendorId
    ? rows.filter((row) => String(row.vendor_id || row.vendor_name) === String(vendorId))
    : rows;

  const chartData = useMemo(() => {
    const buckets = new Map();
    chartRows.forEach((row) => {
      const key = toYearMonth(row.start_period) || "unknown";
      if (!buckets.has(key)) buckets.set(key, { name: monthLabel(key), inProgress: 0, completed: 0, pending: 0 });
      buckets.get(key)[statusBucket(row.status)] += 1;
    });
    return Array.from(buckets.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, value]) => value);
  }, [chartRows]);

  const counts = useMemo(() => {
    return rows.reduce((total, row) => {
      total[statusBucket(row.status)] += 1;
      return total;
    }, { inProgress: 0, completed: 0, pending: 0 });
  }, [rows]);

  const totalAudits = rows.length;
  const inProgressPct = percent(counts.inProgress, totalAudits);
  const completedPct = percent(counts.completed, totalAudits);
  const pendingPct = percent(counts.pending, totalAudits);
  const selectedVendor = vendorOptions.find((vendor) => vendor.id === vendorId);

  const notices = useMemo(() => {
    const list = [...rows].sort((a, b) => {
      const aTime = new Date(a.updated_at || a.created_at || 0).getTime();
      const bTime = new Date(b.updated_at || b.created_at || 0).getTime();
      return sortOrder === "oldest" ? aTime - bTime : bTime - aTime;
    });
    return list.slice(0, 8);
  }, [rows, sortOrder]);

  return (
    <div className="container-fluid auditDashboard">
      <div className="row mt-3">
        <div className="col-md-12 ">
          <h5 className="text-white title">Dashboard</h5>
        </div>
        <div className="col-md-12 px-3 cards">
          <div className="row d-flex">
            <div className="col">
              <div className="row cardBox">
                <div className="d-flex justify-content-between">
                  <h4>{getLockedClientId() ? 1 : 0}</h4>
                  <div className="dashboardIconsimg"><img src={OrgImg} alt="" /></div>
                </div>
                <div className="content"><h5>Total Org.</h5></div>
              </div>
            </div>
            <div className="col">
              <div className="row cardBox">
                <div className="d-flex justify-content-between">
                  <h4>{branchCount}</h4>
                  <div className="dashboardIconsimg"><img src={BranchesIcon} alt="" /></div>
                </div>
                <div className="content"><h5>Total Branches</h5></div>
              </div>
            </div>
            <div className="col">
              <div className="row cardBox">
                <div className="d-flex justify-content-between">
                  <h4>{vendorOptions.length}</h4>
                  <div className="dashboardIconsimg"><img src={VendorIcon} alt="" /></div>
                </div>
                <div className="content"><h5>Total Vendors</h5></div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-12 px-3 cards1">
          <div className="row d-flex px-1">
            <div className="col-md-5 pe-1 auditDashboardgraph">
              <div className="cardgraph p-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div><h6 className="chartTitle">Audit Status</h6></div>
                  <select className="vendorSelect form-select form-select-sm" value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
                    <option value="">All vendors</option>
                    {vendorOptions.map((vendor) => (
                      <option key={vendor.id} value={vendor.id}>{vendor.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="chartLegend d-flex align-items-center gap-3">
                    <span><span className="legendDot inProgress"></span>In-progress</span>
                    <span><span className="legendDot completed"></span>Completed</span>
                    <span><span className="legendDot pending"></span>Pending</span>
                  </div>
                  <p className="chartSubTitle">Total Audit</p>
                </div>
                <div className="chartContainer">
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={chartData} barGap={4}>
                      <XAxis dataKey="name" axisLine={false} tickLine={false} />
                      <YAxis hide allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="pending" stackId="a" fill="#A7C7F7" radius={[5, 5, 0, 0]} />
                      <Bar dataKey="completed" stackId="a" fill="#4BA3FF" radius={[5, 5, 0, 0]} />
                      <Bar dataKey="inProgress" stackId="a" fill="#005CFF" radius={[5, 5, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <p className="vendorInfo p-0 m-0">
                  Vendor Name : <span>{selectedVendor?.label || "All vendors"}</span>
                </p>
              </div>
            </div>
            <div className="col-md-3 pe-1 towboxdatarow">
              <div className="towboxdatarowbox1">
                <div className="d-flex justify-content-between">
                  <h4>{totalAudits}</h4>
                  <div className="dashboardIconsimg"><img src={TotalAuditIcon} alt="" /></div>
                </div>
                <h5>Total Audit</h5>
                <p>Audits for your organisation</p>
              </div>
              <div className="mt-3 towboxdatarowbox2">
                <div className="row towboxdatarowbox2bars">
                  <div className="">
                    <h5>Progress Bar</h5>
                    <p>Share of your audits</p>
                  </div>
                  <div className="mb-3 ">
                    <div className="d-flex justify-content-between">
                      <strong>Audit In-Progress</strong>
                      <span>{inProgressPct}%</span>
                    </div>
                    <div style={{ backgroundColor: "#f0f0f0", borderRadius: "10px", height: "7px" }}>
                      <div style={{ width: `${inProgressPct}%`, backgroundColor: "#7166F9", height: "7px", borderRadius: "10px" }}></div>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between">
                      <strong>Completed</strong>
                      <span>{completedPct}%</span>
                    </div>
                    <div style={{ backgroundColor: "#f0f0f0", borderRadius: "10px", height: "7px" }}>
                      <div style={{ width: `${completedPct}%`, backgroundColor: "#00BFA6", height: "7px", borderRadius: "10px" }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="d-flex justify-content-between">
                      <strong>Pending</strong>
                      <span>{pendingPct}%</span>
                    </div>
                    <div style={{ backgroundColor: "#f0f0f0", borderRadius: "10px", height: "7px" }}>
                      <div style={{ width: `${pendingPct}%`, backgroundColor: "#F4C430", height: "7px", borderRadius: "10px" }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4 auditDashboardNotification">
              <div className="">
                <div className="d-flex justify-content-between align-items-center mb-3 sticky-top bg-white">
                  <h5 className="mb-0 title">Notification</h5>
                  <select className="form-select form-select-sm" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                  </select>
                </div>
                {notices.length === 0 ? (
                  <p className="mb-0 px-1">No audits to show.</p>
                ) : notices.map((item) => {
                  const when = formatWhen(item.updated_at || item.created_at);
                  return (
                    <div className="notificationData p-3" key={item.id || `${item.audit_id}-${item.vendor_id}`}>
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-3">
                          <div className="dataIcons">V</div>
                          <div>
                            <p className="mb-1 datatitle">{item.vendor_name} · {item.audit_name}</p>
                            <small>{STATUS_LABEL[item.status] || item.status}</small>
                          </div>
                        </div>
                        <div className="text-end dataDateDate">
                          <p className="mb-0">{when.day}</p>
                          <small>{when.time}</small>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AuditDashboard() {
  const isClient = getSessionUser()?.base_role === "Client";
  if (isClient) return <ClientAuditDashboard />;


    const data = [
        { name: "Jan", inProgress: 40, completed: 30, pending: 20 },
        { name: "Feb", inProgress: 50, completed: 35, pending: 15 },
        { name: "Mar", inProgress: 45, completed: 25, pending: 30 },
        { name: "Apr", inProgress: 55, completed: 40, pending: 20 },
        { name: "May", inProgress: 65, completed: 45, pending: 15 },
        { name: "Jun", inProgress: 80, completed: 50, pending: 25 },
        { name: "Jul", inProgress: 75, completed: 55, pending: 25 },
        { name: "Aug", inProgress: 90, completed: 60, pending: 30 },
        { name: "Sep", inProgress: 60, completed: 40, pending: 20 },
        { name: "Oct", inProgress: 50, completed: 35, pending: 25 },
        { name: "Nov", inProgress: 70, completed: 45, pending: 30 },
        { name: "Dec", inProgress: 60, completed: 50, pending: 20 },
      ];
  return (
    <div className="container-fluid auditDashboard">
        <div className="row mt-3">
            <div className="col-md-12 ">
                <h5 className='text-white title'>Dashboard</h5>

            </div>
            <div className="col-md-12 px-3 cards">
                <div className="row d-flex">
                <div className="col">
                    <div className="row cardBox">
                        <div className="d-flex justify-content-between">
                        <h4>452</h4>
                        <div className="dashboardIconsimg">
                            <img src={OrgImg} alt="" />
                        </div>
                        </div>
                        <div className="content">
                        <h5>Total Org.</h5>
                        <p><span>+3%</span>Than Last Month</p>
                        </div>
                    </div>
                </div>
                <div className="col">
                    <div className="row cardBox">
                        <div className="d-flex justify-content-between">
                        <h4>32</h4>
                        <div className="dashboardIconsimg">
                            <img src={BranchesIcon} alt="" />
                        </div>
                        </div>
                        <div className="content">
                        <h5>Total Branches</h5>
                        <p><span>+5%</span>Than Last Month</p>
                        </div>
                    </div>
                </div>
                <div className="col">
                    <div className="row cardBox">
                        <div className="d-flex justify-content-between">
                        <h4>4523</h4>
                        <div className="dashboardIconsimg">
                            <img src={VendorIcon} alt="" />
                        </div>
                        </div>
                        <div className="content">
                        <h5>Total Vendors</h5>
                        <p><span>+32%</span>Than Last Month</p>
                        </div>
                    </div>
                </div>

                </div>
            </div>
            <div className="col-md-12 px-3 cards1">
                <div className="row d-flex px-1">
                <div className="col-md-5 pe-1 auditDashboardgraph">
                    <div className="cardgraph p-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <div>
                                <h6 className="chartTitle">Lorem Ipsum</h6>
                            </div>
                            <select className="vendorSelect form-select form-select-sm">
                                <option>Vendor Name</option>
                                <option>Vendor 1</option>
                                <option>Vendor 2</option>
                            </select>
                        </div>
                        <div>
                            
                        <div className="chartLegend d-flex align-items-center gap-3">
                            <span><span className="legendDot inProgress"></span>In-progress</span>
                            <span><span className="legendDot completed"></span>Completed</span>
                            <span><span className="legendDot pending"></span>Pending</span>
                            </div>
                            <p className="chartSubTitle">Total Audit</p>
                        </div>

                        <div className="chartContainer">
                        <ResponsiveContainer width="100%" height={250}>
                            <BarChart data={data} barGap={4}>
                            <XAxis dataKey="name" axisLine={false} tickLine={false} />
                            <YAxis hide />
                            <Tooltip />
                            <Bar dataKey="pending" stackId="a" fill="#A7C7F7" radius={[5, 5, 0, 0]} />
                            <Bar dataKey="completed" stackId="a" fill="#4BA3FF" radius={[5, 5, 0, 0]} />
                            <Bar dataKey="inProgress" stackId="a" fill="#005CFF" radius={[5, 5, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                        </div>

                        <p className="vendorInfo p-0 m-0" >
                        Vendor Name : <span>TSM (Taiwan Semiconductor Manufacturing)</span>
                        </p>
                    </div>
                </div>
                    <div className="col-md-3 pe-1 towboxdatarow">
                        <div className='towboxdatarowbox1'>
                            <div className="d-flex justify-content-between">
                                <h4>32</h4>
                                <div className="dashboardIconsimg">
                                    <img src={TotalAuditIcon} alt="" />
                                </div>
                            </div>
                                <h5>Total Audit</h5>
                                <p>Lorem ipsum dolor sit amet consectetur <br /> adipisicing elit.</p>
                        </div>
                        <div className='mt-3 towboxdatarowbox2'>
                            <div className="row towboxdatarowbox2bars">
                                <div className="">
                                    <h5>Progress Bar</h5>
                                    <p>Lorem ipsum dolor sit amet consectetur <br /> adipisicing elit.</p>
                                </div>

                                <div className="mb-3 ">
                                    <div className="d-flex justify-content-between">
                                    <strong>Audit In-Progress</strong>
                                    <span>90%</span>
                                    </div>
                                    <div style={{ backgroundColor: '#f0f0f0', borderRadius: '10px', height: '7px' }}>
                                    <div style={{ width: '90%', backgroundColor: '#7166F9', height: '7px', borderRadius: '10px' }}></div>
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <div className="d-flex justify-content-between">
                                    <strong>Completed</strong>
                                    <span>65%</span>
                                    </div>
                                    <div style={{ backgroundColor: '#f0f0f0', borderRadius: '10px', height: '7px' }}>
                                    <div style={{ width: '65%', backgroundColor: '#00BFA6', height: '7px', borderRadius: '10px' }}></div>
                                    </div>
                                </div>

                                <div>
                                    <div className="d-flex justify-content-between">
                                    <strong>Pending</strong>
                                    <span>20%</span>
                                    </div>
                                    <div style={{ backgroundColor: '#f0f0f0', borderRadius: '10px', height: '7px' }}>
                                    <div style={{ width: '20%', backgroundColor: '#F4C430', height: '7px', borderRadius: '10px' }}></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4 auditDashboardNotification">
                        <div className="">
                            <div className="d-flex justify-content-between align-items-center mb-3 sticky-top bg-white">
                            <h5 className="mb-0 title">Notification</h5>
                            <select
                                className="form-select form-select-sm"
                            >
                                <option>Sort by Time</option>
                                <option>Newest First</option>
                                <option>Oldest First</option>
                            </select>
                            </div>
                            {[
                            { id: 1, name: "Vendor 1", color: "#E9E7FF" },
                            { id: 2, name: "Vendor 2", color: "#F4F9FF" },
                            { id: 3, name: "Vendor 3", color: "#F4F9FF" },
                            { id: 4, name: "Vendor 4", color: "#F4F9FF" },
                            { id: 1, name: "Vendor 1", color: "#E9E7FF" },
                            { id: 2, name: "Vendor 2", color: "#F4F9FF" },
                            { id: 3, name: "Vendor 3", color: "#F4F9FF" },
                            { id: 4, name: "Vendor 4", color: "#F4F9FF" },
                            ].map((item, index) => (
                            <div className='notificationData p-3'>
                                <div
                                key={index}
                                className=" d-flex justify-content-between align-items-center "
                            >
                                <div className="d-flex align-items-center gap-3">
                                <div className='dataIcons'
                                >
                                    V{item.id}
                                </div>
                                <div>
                                    <p className="mb-1 datatitle">
                                    {item.name} Completed That Form
                                    </p>
                                </div>
                                </div>
                                <div className="text-end dataDateDate">
                                <p className="mb-0">
                                    28 Sep 2025
                                </p>
                                <small>04:44:33</small>
                                </div>
                            </div>
                            <button
                            className="btn btn-sm"
                            >
                            Validate
                            </button>
                            </div>
                            ))}
                        </div>
                    </div>

                </div>
            </div>
        </div>
    </div>
  )
}

export default AuditDashboard